"use client";

import React, { useEffect, useRef, useState } from "react";
import { Info, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const chartData = [{
  year: "Year 1",
  yield: 8.0
}, {
  year: "Year 2",
  yield: 8.4
}, {
  year: "Year 3",
  yield: 8.9
}, {
  year: "Year 4",
  yield: 9.5
}, {
  year: "Year 5",
  yield: 10.2
}];
const chartConfig = {
  yield: {
    label: "Net Yield (%)",
    color: "hsl(145 45% 45%)"
  }
};
export default function FinancialBenefits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="financial-benefits" className="bg-background py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 md:mb-16 max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Robust Financial Architecture
          </h2>
          <p className="text-muted-foreground text-lg">
            Our investment models are engineered for resilience, offering
            inflation-protected yields and hands-off management for complete
            peace of mind.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 auto-rows-[minmax(180px,auto)]">
          
          {}
          <Card className="lg:col-span-2 lg:row-span-2 bg-secondary text-secondary-foreground border-white/10 shadow-xl overflow-hidden flex flex-col relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <CardHeader className="pb-2 z-10">
              <CardTitle className="text-4xl md:text-5xl font-bold font-default text-white tracking-tight">
                8-10%
                <span className="block text-xl md:text-2xl text-white/70 font-medium mt-1">
                  Net Yield
                </span>
              </CardTitle>
              <p className="text-white/60 text-sm mt-4 max-w-md">
                Consistent, high-performing returns backed by robust market
                fundamentals and strategic asset selection.
              </p>
            </CardHeader>
            <CardContent className="flex-1 p-0 mt-6 min-h-[200px] relative z-10">
              <ChartContainer config={chartConfig} className="h-full w-full absolute inset-0 pb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{
                  top: 20,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="colorYield" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-yield)" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="var(--color-yield)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Area type="monotone" dataKey="yield" stroke="var(--color-yield)" strokeWidth={3} fill="url(#colorYield)" animationDuration={1500} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {}
          <Card className="lg:col-span-1 lg:row-span-1 bg-secondary text-secondary-foreground border-white/10 shadow-xl flex flex-col justify-between relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity duration-300">
              <TrendingUp className="w-24 h-24 -mr-8 -mt-8 text-white" />
            </div>
            <CardHeader className="relative z-10 pb-0">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger className="text-left cursor-help group/trigger flex items-center gap-2">
                    <CardTitle className="text-2xl font-bold font-default text-white border-b border-dashed border-white/30 group-hover/trigger:border-white transition-colors">
                      CPI-Linked
                    </CardTitle>
                    <Info className="w-4 h-4 text-white/50 group-hover/trigger:text-white transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[250px] p-4 bg-popover text-popover-foreground border-border shadow-xl">
                    <p className="text-sm font-medium mb-1">Consumer Price Index</p>
                    <p className="text-xs text-muted-foreground">
                      Rental income increases annually in line with inflation,
                      protecting the real value of your yield over time.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="relative z-10 mt-4">
              <p className="text-white/60 text-sm leading-relaxed">
                Built-in inflation protection ensuring your returns maintain
                their purchasing power.
              </p>
            </CardContent>
          </Card>

          {}
          <Card className="lg:col-span-1 lg:row-span-1 bg-secondary text-secondary-foreground border-white/10 shadow-xl flex flex-col justify-between relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-40 transition-opacity duration-300">
              <ShieldCheck className="w-24 h-24 -mr-8 -mt-8 text-white" />
            </div>
            <CardHeader className="relative z-10 pb-0">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger className="text-left cursor-help group/trigger flex items-center gap-2">
                    <CardTitle className="text-2xl font-bold font-default text-white border-b border-dashed border-white/30 group-hover/trigger:border-white transition-colors">
                      FRI Lease
                    </CardTitle>
                    <Info className="w-4 h-4 text-white/50 group-hover/trigger:text-white transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[250px] p-4 bg-popover text-popover-foreground border-border shadow-xl">
                    <p className="text-sm font-medium mb-1">Fully Repairing & Insuring</p>
                    <p className="text-xs text-muted-foreground">
                      The tenant bears full responsibility for all property repairs,
                      maintenance, and building insurance costs.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="relative z-10 mt-4">
              <p className="text-white/60 text-sm leading-relaxed">
                Zero unexpected maintenance costs. Your net yield is exactly
                what you receive.
              </p>
            </CardContent>
          </Card>

          {}
          <Card className="lg:col-span-2 lg:row-span-1 bg-secondary text-secondary-foreground border-white/10 shadow-xl relative overflow-hidden min-h-[220px] group">
            <div className="absolute inset-0 bg-secondary z-0" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/professional_management_team.png" alt="Professional Management Team" className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity group-hover:opacity-40 group-hover:scale-105 transition-all duration-700 z-0" />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/80 to-transparent z-10" />
            
            <div className="relative z-20 h-full flex flex-col justify-end p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold font-default text-white">
                  Fully Managed
                </CardTitle>
              </div>
              <p className="text-white/80 text-sm md:text-base max-w-lg leading-relaxed">
                A completely hands-off investment experience. Our professional
                management team handles all day-to-day operations, tenant
                relations, and compliance.
              </p>
            </div>
          </Card>

        </div>
      </div>
    </section>;
}